import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { SERVICES } from '../../lib/site';
import { imageForServiceKey, slotForServiceKey } from '../../lib/imagery';
import { useBrandImage } from '../../lib/BrandImagesContext';

function ServiceCard({ s, i }) {
    const Icon = s.icon;
    const img = useBrandImage(slotForServiceKey(s.key), imageForServiceKey(s.key));
    return (
        <motion.article
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5, delay: (i % 3) * 0.05 }}
            whileHover={{ y: -3 }}
            className="group rounded-2xl dt-glass overflow-hidden hover:border-primary/40 hover:shadow-[0_0_0_1px_rgba(0,229,255,0.32),0_18px_60px_rgba(0,0,0,0.55)] transition-shadow"
            data-testid={`service-card-${s.key}`}
        >
            {/* Header image — human anchor */}
            <div className="relative aspect-[16/8] overflow-hidden">
                <img
                    src={img}
                    alt=""
                    aria-hidden
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    style={{ filter: 'saturate(0.6) hue-rotate(-6deg) brightness(0.65)' }}
                    onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[rgba(8,12,20,0.98)] via-[rgba(8,12,20,0.55)] to-[rgba(8,12,20,0.15)]" />
                <span className="absolute top-3 right-3 font-mono text-[10px] tracking-[0.22em] text-primary bg-primary/10 border border-primary/30 px-2 py-0.5 rounded-full">
                    {s.tag.toUpperCase()}
                </span>
            </div>
            <div className="p-6 -mt-8 relative">
                <div className="flex items-center justify-between">
                    <span className="h-11 w-11 rounded-xl bg-primary/10 border border-primary/30 inline-flex items-center justify-center text-primary group-hover:rotate-6 transition-transform backdrop-blur-md">
                        <Icon className="h-5 w-5" />
                    </span>
                </div>
                <h3 className="font-display mt-5 text-lg sm:text-xl font-semibold">{s.title}</h3>
                <p className="mt-2 text-sm text-foreground/75 leading-relaxed">{s.body}</p>
                <div className="mt-6 inline-flex items-center gap-1.5 text-sm text-primary group-hover:text-accent">
                    Explore
                    <ArrowUpRight className="h-4 w-4" />
                </div>
            </div>
        </motion.article>
    );
}

export default function ServicesGrid() {
    return (
        <section
            id="services"
            data-testid="home-services"
            className="relative py-20 lg:py-28"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
                    <div>
                        <span className="dt-section-eyebrow">WHAT WE DELIVER</span>
                        <h2 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
                            Integrated technology solutions
                            <br /> built around <span className="dt-gradient-text">real operations</span>.
                        </h2>
                    </div>
                    <Link
                        to="/services"
                        className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-accent"
                    >
                        View all services <ArrowUpRight className="h-4 w-4" />
                    </Link>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {SERVICES.map((s, i) => <ServiceCard key={s.key} s={s} i={i} />)}
                </div>
            </div>
        </section>
    );
}
